import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
// import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import {
  convertUTCtoTimezoneDate,
  convertUTCtoTimezoneTime,
} from '../../../shared-functions/TimezoneConversions';
import tariffApi from '../../../utils/api/tariff';

// interface PriceComponentTableInterface {
//   id: string;
//   componentType: string;
//   componentName: string;
//   valueType: string;
//   value: string;
//   order: number;
// }

// interface TimeBasedPricingTableInterface {
//   id: number;
//   name: string;
//   startTime: string;
//   endTime: string;
//   recurrenceType: string;
//   date: string;
//   days: string;
//   priceMultiplier: string;
// }

interface ViewTariffProperties {
  id?: string;
  isSidePanel?: boolean;
}

/**
 * Component for displaying view tariffs
 * @param {any} properties - View tariff properties
 * @returns {any} View tariffs component
 */
function ViewTariff(properties: ViewTariffProperties = {}) {
  const timeSlotView = false;
  const { id, isSidePanel } = properties;
  const [searchParameters] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: parameterId } = useParams<{ id: string }>();
  const tariffId = id || searchParameters.get('tariffId') || parameterId;
  const [tariffDetails, setTariffDetails] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);

  // Calculator states
  const isPartner = searchParameters.get('isPartner') === 'true';
  const isExtended = searchParameters.get('isExtended') === 'true' || !isSidePanel;
  const [calculatorData, setCalculatorData] = useState<any>({
    powerConsumed: '',
    sessionStartDateTime: '',
    sessionEndDateTime: '',
  });
  const [calculating, setCalculating] = useState(false);
  const [calcResults, setCalcResults] = useState<any>({ totalAmount: '100.00' });

  // // Handle sidepanel resize when extended
  // useEffect(() => {
  //   let sidePanelElement: HTMLElement | null = null;
  //   let tableElement: HTMLElement | null = null;
  //   // if (isSidePanel) {
  //   //   sidePanelElement = document.querySelector('.custom-table-side-panel') as HTMLElement;
  //   //   tableElement = document.querySelector('.custom-table') as HTMLElement;
  //   //   if (sidePanelElement && tableElement) {
  //   //     if (isExtended) {
  //   //       sidePanelElement.style.setProperty('width', '70%', 'important');
  //   //       tableElement.style.setProperty('width', '30%', 'important');
  //   //     } else {
  //   //       sidePanelElement.style.setProperty('width', '50%', 'important');
  //   //       tableElement.style.setProperty('width', '50%', 'important');
  //   //     }
  //   //   }
  //   // }
  //   return () => {
  //     if (sidePanelElement && tableElement) {
  //       sidePanelElement.style.removeProperty('width');
  //       tableElement.style.removeProperty('width');
  //     }
  //   };
  // }, [isExtended, isSidePanel]);

  /**
   * Update calculator data
   * @param {string} field - Field to update
   * @param {any} value - New value
   */
  const updateCalculatorData = (field: string, value: string | number) => {
    setCalculatorData((previous: any) => ({ ...previous, [field]: value }));
  };

  /**
   * Handle calculate tariff based on power consumed and session start time
   */
  const handleCalculate = () => {
    const requestData = {
      startDateUTC: calculatorData.sessionStartDateTime
        ? new Date(calculatorData.sessionStartDateTime).toISOString()
        : '',
      //  endDateUTC: calculatorData.sessionEndDateTime
      //    ? new Date(calculatorData.sessionEndDateTime).toISOString()
      //    : '',
      unitsConsumed: Number(calculatorData.powerConsumed),
    };
    if (typeof tariffId === 'string') {
      setCalculating(true);
      tariffApi
        .fetchSingerTariffCalculatorData(tariffId, requestData, isPartner)
        .then((response) => {
          setCalculating(false);
          const data = response.data || {};

          data.priceBreakdown = response?.data?.amountSplit?.map((split: any) => ({
            componentName: split.componentName,
            amount: `${split.contributedAmount.toFixed(2)} ${data.currency}`,
          })) || [
            { componentName: 'Base Price', amount: '0.00 SAR' },
            { componentName: 'Operating Cost', amount: '0.00 SAR' },
          ];
          if (response.data.timeBasedPricing.slotName) {
            data.timeBasePrice = [
              {
                componentName: response?.data?.timeBasedPricing?.slotName || 'Additional Charges',
                amount: `${response?.data?.timeBasedPricing?.additionalAmount?.toFixed(2)} ${data.currency}`,
              },
            ];
          }
          data.totalAmount =
            `${response?.data?.totalAmount?.toFixed(2)} ${data.currency}` || '0.00 SAR';
          setCalcResults(data);
        })
        .catch((error: any) => {
          setCalculating(false);
          dispatch(
            setNotificationMessage({
              status: true,
              message: error?.response?.data?.message || 'Failed to calculate tariff',
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  useEffect(() => {
    if (tariffId) {
      setLoading(true);
      tariffApi
        .fetchSingleTariff(tariffId, isPartner)
        .then((response) => {
          const rawScopeLevels = response?.data?.scopeLevels;
          let parsedScopeLevels: any[] = [];
          if (Array.isArray(rawScopeLevels)) {
            parsedScopeLevels = rawScopeLevels;
          } else if (typeof rawScopeLevels === 'string') {
            try {
              parsedScopeLevels = JSON.parse(rawScopeLevels);
            } catch {
              parsedScopeLevels = [];
            }
          }

          const output = {
            // status: response?.data?.isActive,
            status: response?.data?.status,
            enforcementDate: convertUTCtoTimezoneDate(
              response?.data?.effectiveFrom,
              response?.data?.timezone,
            ),
            expiryDate: convertUTCtoTimezoneDate(
              response?.data?.effectiveTo,
              response?.data?.timezone,
            ),
            tariffName: response?.data?.name,
            tariffType: response?.data?.type,
            currentType: response?.data?.currentType,
            accountCustomId: response?.data?.accountCustomId,
            connectorRating: `${response?.data?.connectorRating?.currentType} ${response?.data?.connectorRating?.capacityKW} ${response?.data?.connectorRating?.unit}`,
            scopeLevels: response?.data?.scopeLevels,
            country: parsedScopeLevels?.find((level: any) => level.scope === 'country')?.scopeName,
            state: parsedScopeLevels?.find((level: any) => level.scope === 'state')?.scopeName,
            city: parsedScopeLevels?.find((level: any) => level.scope === 'city')?.scopeName,
            stationId: response?.data?.stationId,
            accountName: response?.data?.accountCustomId,
            scope: response?.data?.scope,
            scopeId: response?.data?.scopeId,
            scopeName: response?.data?.scopeName,
            currency: response?.data?.currency,
            priceComponents: response?.data?.tariffPricingComponent.map(
              (comp: any, index: number) => ({
                id: index + 1,
                componentType: comp.componentType,
                componentName: comp.componentName,
                valueType: comp.valueType,
                value: comp.value,
                order: comp.order,
              }),
            ),
            timeBasedPricingSlots: (
              response?.data?.timeBasedPricingDetails ||
              response?.data?.timeBasedPricingSlots ||
              []
            ).map((slot: any, index: number) => ({
              id: index + 1,
              name: slot.name,
              startTime: convertUTCtoTimezoneTime(slot.startTime, response?.data?.timezone),
              endTime: convertUTCtoTimezoneTime(slot.endTime, response?.data?.timezone),
              recurrenceType: slot.recurrenceType,
              applicableOn:
                slot.dates && slot.dates.length > 0
                  ? slot.dates.map((date: string) => ConvertDDMMYYYYtoISO(date)).join(', ')
                  : '-',
              days: Array.isArray(slot.daysOfWeek) ? slot.daysOfWeek.join(' | ') : '-',
              priceMultiplier: String(slot.priceMultiplier),
            })),
          };
          setTariffDetails(output);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [tariffId, isPartner]);

  if (loading) {
    return (
      <div
        className={`view-tariff-wrapper ${isSidePanel ? 'sidepanel-mode' : ''} ${isExtended ? 'extended-mode' : ''}`}>
        {!isSidePanel && (
          <PageTitle
            title="Tariff Details"
            topRightContent={
              <div className="page-title-button-group">
                <MyButton
                  label="Cancel"
                  buttonType="outline"
                  onClickFunc={() => navigate('/ev-admin/tariffs')}
                />
              </div>
            }
          />
        )}
        <div className="view-tariff-card loader-card">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`view-tariff-wrapper ${isSidePanel ? 'sidepanel-mode' : ''} ${isExtended ? 'extended-mode' : ''}`}>
      {!isSidePanel && (
        <PageTitle
          title="Tariff Details"
          topRightContent={
            <div className="page-title-button-group">
              <MyButton
                label="Cancel"
                buttonType="outline"
                onClickFunc={() => navigate('/ev-admin/tariffs')}
              />
              {tariffDetails.status !== 'expired' && (
                <MyButton
                  label="Edit Tariff"
                  buttonType="submit"
                  onClickFunc={() =>
                    navigate(
                      `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
                    )
                  }
                />
              )}
            </div>
          }
        />
      )}

      <div className="view-tariff-card">
        <div className="extended-layout-container">
          <div className="details-left-column">
            {/* {isSidePanel && tariffDetails.tariffName && (
              <div className="tariff-hero-banner">
                <div className="hero-details">
                  <h3 className="tariff-name-title">{tariffDetails.tariffName}</h3>
                  {tariffDetails.tariffType && (
                    <span className="tariff-type-badge">{tariffDetails.tariffType}</span>
                  )}
                </div>
                {tariffDetails.status && (
                  <div className={`status-badge ${tariffDetails.status}`}>
                    <span className="status-dot" />
                    <span>{tariffDetails.status}</span>
                  </div>
                )}
              </div>
            )} */}
            <div className="details-section">
              <p className="section-title">Basic Details</p>
              <div className="details-grid three-column">
                <div className="detail-item">
                  <p className="label">Tariff Name</p>
                  <p className="value">{tariffDetails.tariffName || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Tariff Type</p>
                  <p className="value">{tariffDetails.tariffType || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Country</p>
                  <p className="value">{tariffDetails.country || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">State</p>
                  <p className="value">{tariffDetails.state || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">City</p>
                  <p className="value">{tariffDetails.city || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Scope Id</p>
                  <p className="value">{tariffDetails.scopeId || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Scope Name</p>
                  <p className="value">{tariffDetails.scopeName || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Created By</p>
                  <p className="value">{tariffDetails.createdBy || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Edited By</p>
                  <p className="value">{tariffDetails.editedBy || '-'}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <p className="section-title">Technical Details</p>

              <div className="details-grid two-column">
                <div className="detail-item">
                  <p className="label">Current Type</p>
                  <p className="value">{tariffDetails.currentType || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Connector Rating (kW)</p>
                  <p className="value">{tariffDetails.connectorRating || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Enforcement Date</p>
                  <p className="value">{tariffDetails.enforcementDate || '-'}</p>
                </div>

                <div className="detail-item">
                  <p className="label">Expiry Date</p>
                  <p className="value">{tariffDetails.expiryDate || '-'}</p>
                </div>
              </div>
            </div>

            <div className="details-section">
              <p className="section-title">Price Components</p>

              <div className="simple-table">
                <div className="simple-table-header">
                  <div className="col-1-5">Component Type</div>
                  <div className="col-1-5">Component Name</div>
                  <div className="col-1-5">Value Type</div>
                  <div className="col-1">Value</div>
                  <div className="col-0-5">Order</div>
                </div>
                {tariffDetails?.priceComponents?.map((comp: any) => (
                  <div className="simple-table-row" key={comp.id}>
                    <div className="col-1-5">{comp.componentType || '-'}</div>
                    <div className="col-1-5">{comp.componentName || '-'}</div>
                    <div className="col-1-5">{comp.valueType || '-'}</div>
                    <div className="col-1">
                      {comp.value !== undefined && comp.value !== null
                        ? `${Number(comp.value).toFixed(2)} ${tariffDetails.currency || ''}`
                        : '-'}
                    </div>
                    <div className="col-0-5">{comp.order ?? '-'}</div>
                  </div>
                ))}
                {(!tariffDetails?.priceComponents ||
                  tariffDetails.priceComponents.length === 0) && (
                  <div className="simple-table-row">
                    <div className="col-1">No Price Components found</div>
                  </div>
                )}
              </div>
            </div>

            {timeSlotView && (
              <div className="details-section">
                <p className="section-title">Time Based Pricing Slots</p>

                <div className="pricing-slots-list">
                  {!tariffDetails?.timeBasedPricingSlots ||
                  tariffDetails.timeBasedPricingSlots.length === 0 ? (
                    <div className="pricing-slot-item">
                      <div className="details-grid three-column">
                        <div className="detail-item">
                          <p className="label">Slot Name</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">Start Time</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">End Time</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">Recurrence Type</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">Applicable On</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">Days</p>
                          <p className="value">-</p>
                        </div>
                        <div className="detail-item">
                          <p className="label">Multiplier</p>
                          <p className="value">-</p>
                        </div>
                      </div>
                      <div style={{ marginTop: '24px' }}>
                        <p className="no-data-text">No Time Based Pricing Slots found</p>
                      </div>
                    </div>
                  ) : (
                    tariffDetails.timeBasedPricingSlots.map((slot: any, index: number) => (
                      <div className="pricing-slot-item" key={slot.id}>
                        {index > 0 && <hr className="slot-divider" />}
                        <div className="details-grid three-column">
                          <div className="detail-item">
                            <p className="label">Slot Name</p>
                            <p className="value">{slot.name || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">Start Time</p>
                            <p className="value">{slot.startTime || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">End Time</p>
                            <p className="value">{slot.endTime || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">Recurrence Type</p>
                            <p className="value">{slot.recurrenceType || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">Applicable On</p>
                            <p className="value">{slot.applicableOn || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">Days</p>
                            <p className="value">{slot.days || '-'}</p>
                          </div>
                          <div className="detail-item">
                            <p className="label">Multiplier</p>
                            <p className="value">{slot.priceMultiplier || '-'}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {isExtended && (
            <div className="calculator-right-column">
              <div className="calculator-panel">
                <h3 className="section-title">Calculate Tariff</h3>

                <div className="form-field">
                  <CustomDatePicker
                    inputLabel="Session Start Date & Time"
                    placeholder="DD/MM/YYYY - 00:00"
                    requiredTime
                    name="sessionStartDateTime"
                    value={calculatorData.sessionStartDateTime}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="form-field">
                  <CustomDatePicker
                    inputLabel="Session End Date & Time"
                    placeholder="DD/MM/YYYY - 00:00"
                    requiredTime
                    name="sessionEndDateTime"
                    value={calculatorData.sessionEndDateTime}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="form-field">
                  <CustomInput
                    inputLabel="Power Consumed"
                    placeholder="Eg: 10"
                    name="powerConsumed"
                    type="text"
                    value={calculatorData.powerConsumed}
                    onChangeValue={(name, value) => updateCalculatorData(name, value)}
                    valueRequired
                  />
                </div>

                <div className="calculate-action">
                  <MyButton
                    label="Calculate"
                    buttonType="submit"
                    onClickFunc={handleCalculate}
                    disableButton={calculating}
                    loading={calculating}
                  />
                </div>

                <div className="price-breakdown-card">
                  <p className="price-breakdown-title">Price Breakdown</p>

                  <div className="breakdown-table-header">
                    <span>Component</span>
                    <span>Amount</span>
                  </div>

                  <div className="breakdown-rows-list">
                    {calcResults.priceBreakdown?.map((comp: any, index: number) => (
                      <div className="breakdown-row-item" key={index}>
                        <span className="comp-name">{comp.componentName}</span>
                        <span className="comp-amt">{comp.amount}</span>
                      </div>
                    )) || null}
                  </div>

                  {timeSlotView && (
                    <>
                      <div className="breakdown-table-header pricing-slots-header">
                        <span>Pricing Slot</span>
                        <span>Amount</span>
                      </div>

                      <div className="breakdown-rows-list">
                        {calcResults.timeBasePrice?.map((comp: any, index: number) => (
                          <div className="breakdown-row-item" key={index}>
                            <span className="comp-name">{comp.componentName}</span>
                            <span className="comp-amt">{comp.amount}</span>
                          </div>
                        )) || null}
                      </div>
                    </>
                  )}

                  <div className="breakdown-total-footer">
                    <span className="footer-label">Total Amount</span>
                    <span className="footer-value">
                      {parseFloat(calcResults.totalAmount).toFixed(2)} {calcResults.currency}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isSidePanel && (
        <div className="sidepanel-footer-actions">
          <MyButton
            label="Verify Tariff"
            buttonType="outline"
            onClickFunc={() => {
              navigate(`/ev-admin/tariffs/view/${tariffId}?isExtended=true`);
            }}
          />
          <MyButton
            label="Edit Tariff"
            buttonType="submit"
            onClickFunc={() =>
              navigate(
                `/ev-admin/tariffs/edit-tariff/${tariffId}${isPartner ? '?isPartner=true' : ''}`,
              )
            }
            disableButton={
              !permissionCheck('partnerTariffUpdate') && !permissionCheck('cpoTariffUpdate')
            }
          />
        </div>
      )}
    </div>
  );
}

ViewTariff.defaultProps = {
  id: '',
  isSidePanel: false,
};

export default ViewTariff;
